import React, { useState } from "react";
import { ShieldCheck, Loader2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

/**
 * Blocking overlay shown when an admin-created (or admin-reset) user must set
 * their own password before using the app. No current-password is required —
 * the forced first-login change is verified server-side via must_change_password.
 */
export default function ForcePasswordModal() {
  const { changePassword } = useAuth();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const tooShort = pw.length > 0 && pw.length < 8;
  const mismatch = confirm.length > 0 && pw !== confirm;
  const valid = pw.length >= 8 && pw === confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    try {
      await changePassword({ new_password: pw });
      toast.success("Password set. Welcome aboard.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't set your password.");
      setBusy(false);
    }
  };

  const labelStyle = { display: "block", fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ink-dim)", marginBottom: 6 };

  return (
    <div
      data-testid="force-pw-modal"
      style={{
        position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center",
        background: "rgba(12,12,16,0.72)", backdropFilter: "blur(6px)", padding: 24,
      }}
    >
      <div className="fade-up" style={{
        width: "100%", maxWidth: 440, background: "var(--panel)", border: "1px solid var(--hairline)",
        borderRadius: 16, boxShadow: "0 24px 60px rgba(0,0,0,0.4)", overflow: "hidden",
      }}>
        <div style={{ padding: "26px 26px 18px" }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12, background: "rgba(78,205,196,0.14)",
            display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16,
          }}>
            <ShieldCheck size={22} style={{ color: "var(--ph-product-teal)" }} />
          </div>
          <h3 style={{ marginBottom: 6 }}>Set your password</h3>
          <div className="text-soft" style={{ fontSize: 13.5, lineHeight: 1.6 }}>
            For your security, choose a new password before you continue. You won&apos;t need the temporary one again.
          </div>
        </div>

        <form onSubmit={submit} style={{ padding: "0 26px 26px", display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={labelStyle}>New password</label>
            <div style={{ position: "relative" }}>
              <input
                className="field"
                type={show ? "text" : "password"}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder="At least 8 characters"
                data-testid="force-pw-new"
                autoFocus
              />
              <button type="button" onClick={() => setShow((s) => !s)}
                style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "transparent", border: "none", cursor: "pointer", color: "var(--ink-dim)" }}
                aria-label={show ? "Hide password" : "Show password"}>
                {show ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {tooShort && <div style={{ color: "var(--gold)", fontSize: 12, marginTop: 6 }}>Use at least 8 characters.</div>}
          </div>
          <div>
            <label style={labelStyle}>Confirm password</label>
            <input
              className="field"
              type={show ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Re-enter your password"
              data-testid="force-pw-confirm"
            />
            {mismatch && <div style={{ color: "var(--gold)", fontSize: 12, marginTop: 6 }}>Passwords don&apos;t match.</div>}
          </div>
          <button className="btn btn-primary" type="submit" disabled={!valid || busy} data-testid="force-pw-submit"
            style={{ justifyContent: "center", padding: "12px 16px", marginTop: 4 }}>
            {busy ? <Loader2 size={15} className="spin" /> : <ShieldCheck size={15} />}
            {busy ? "Saving…" : "Set password & continue"}
          </button>
        </form>
      </div>
    </div>
  );
}
